<template>
    <div
        ref="menuItemsContainer"
        class="menu_items__container"
        :class="{'menu_items__collapsed': collapse, 'menu_items__mobile': isMobile}"
        :style="{'padding-bottom: 48px': isUserAuth}"
    >
        <div v-if="isUserAuth" class="menu_items__favorites_container">
            <PanelMenu
                v-show="isUserAuth"
                :model="getFavoriteModel"
                :expanded-keys="favoriteExpandedKeys"
                @update:expanded-keys="onToggleFavoriteExpanded"
                :pt="{
                    root: {
                        class: (collapse ? 'overflow-visible' : ''),
                    },
                    panel: {class: {'overflow-visible': collapse, 'md:block': true}},
                }"
            >
                <template #item="{item, active, hasSubmenu}">
                    <template v-if="collapse">
                        <a
                            class="menu_items__item_collapsed"
                            :class="{'cursor-pointer': hasSubmenu && !hasFavoriteItems}"
                            :href="item?.url"
                            @mouseenter="calcMaxHeightCollapsedSubmenu"
                        >
                            <div class="menu_items__item_label"><i :class="item?.icon"></i></div>
                            <div
                                class="menu_items__collapsed_submenu"
                                :class="{'menu_items__collapsed_with_items': hasSubmenu && hasFavoriteItems}"
                            >
                                <div class="menu_items__item_label">
                                    <span class="menu_items__collapsed_title">{{ item.label }}</span>
                                </div>
                                <template v-if="hasSubmenu">
                                    <a
                                        v-for="submenuItem in hasSubmenu"
                                        :href="submenuItem.onClick && !submenuItem.url ? '#' : submenuItem.url"
                                        class="menu_items__item_label"
                                        @click="onMenuItemClick($event, submenuItem)"
                                    >
                                        <i :class="submenuItem.icon"></i>
                                        <span>{{ submenuItem.label }}</span>
                                    </a>
                                </template>
                            </div>
                        </a>
                    </template>
                    <a
                        v-else
                        class="menu_items__item"
                        :href="item?.onClick && !item?.url ? '#' : item?.url"
                        @click="onMenuItemClick($event, item)"
                    >
                        <div
                            class="menu_items__item_label"
                            :class="{
                                'menu_items__item_new': item?.isNew,
                                'menu_items__item_beta': item?.isBeta,
                            }"
                        >
                            <i :class="item?.icon"></i>
                            <span>{{ item?.label }}</span>
                        </div>
                        <template v-if="hasSubmenu">
                            <template v-if="hasFavoriteItems">
                                <!-- v-show необходим, т.к. при исчезновении элемента из DOM при фокусе - закрывается меню -->
                                <i v-show="active" class="pi pi-chevron-up"></i>
                                <i v-show="!active" class="pi pi-chevron-down"></i>
                            </template>
                            <i v-else class="pi pi-plus"></i>
                        </template>
                    </a>
                </template>
            </PanelMenu>
            <div class="menu_items__favorite_divider overflow-visible my-3" ref="favoriteDivider">
                <Divider />
                <div v-if="!isMobile" class="menu_items__button_collapse" @click="onClickCollapseButton">
                    <i class="pi pi-angle-double-left" :class="{'rotate-180': collapse}"></i>
                </div>
            </div>
        </div>
        <PanelMenu
            :model="getModel"
            v-model:expanded-keys="expandedKeys"
            @update:expanded-keys="updateExpandedKeys"
            class="menu_items__menu_container"
            :pt="{
                root: {
                    class: (collapse ? 'overflow-visible' : ''),
                },
                panel: {class: (collapse ? 'overflow-visible' : '')},
            }"
        >
            <template #item="{item, active, hasSubmenu}">
                <Divider v-if="item.separator" />
                <template v-else-if="collapse">
                    <a class="menu_items__item_collapsed" :href="item.url" @mouseenter="calcMaxHeightCollapsedSubmenu">
                        <div class="menu_items__item_label"><i :class="item.icon"></i></div>
                        <div
                            class="menu_items__collapsed_submenu"
                            :class="{'menu_items__collapsed_with_items': hasSubmenu}"
                        >
                            <div class="menu_items__item_label">
                                <span class="menu_items__collapsed_title">{{ item.label }}</span>
                            </div>
                            <template v-if="hasSubmenu">
                                <a
                                    v-for="submenuItem in hasSubmenu"
                                    :href="submenuItem.onClick && !submenuItem.url ? '#' : submenuItem.url"
                                    class="menu_items__item_label"
                                    @click="onMenuItemClick($event, submenuItem)"
                                >
                                    <i :class="submenuItem.icon"></i>
                                    <span>{{ submenuItem.label }}</span>
                                </a>
                            </template>
                        </div>
                    </a>
                </template>
                <a
                    v-else
                    class="menu_items__item"
                    :href="item.onClick && !item.url ? '#' : item.url"
                    @click="onMenuItemClick($event, item)"
                >
                    <div
                        class="menu_items__item_label"
                        :class="{
                            'menu_items__item_new': item.isNew,
                            'menu_items__item_beta': item.isBeta,
                        }"
                    >
                        <i :class="item.icon"></i>
                        <span>{{ item.label }}</span>
                    </div>
                    <template v-if="hasSubmenu">
                        <!-- v-show необходим, т.к. при исчезновении элемента из DOM при фокусе - закрывается меню -->
                        <i v-show="active" class="pi pi-chevron-up"></i>
                        <i v-show="!active" class="pi pi-chevron-down"></i>
                    </template>
                </a>
            </template>
        </PanelMenu>
        <div v-if="isUserAuth && !isMobile" class="menu_items__icon_settings_container" @click="onClickSettings">
            <i class="pi pi-ellipsis-h"></i>
            <span v-if="!collapse">{{ $t('lang.menu.left_menu_label') }}</span>
        </div>
    </div>
</template>

<script>
import PanelMenu from "primevue/panelmenu";
import {ArrayHelper} from "../../../helpers/ArrayHelper";
import Divider from "primevue/divider";
import {Listeners} from "../../../constants/listeners";
import MenuSettings from "./Settings.vue";
import UserApi from "../../../api/UserApi";
import {nextTick} from "vue";
import {StorageKeys} from "../../../constants/storageKeys";
import BrowserStorage from "../../../mixins/BrowserStorage";
import {MenuItemsIds} from "../constants/MenuItemsIds";
import debounce from "lodash/debounce";
import {AppConstant} from "../../../app.constant";
import Alerts from "../../../mixins/Alerts";
import Verbox from "../../../mixins/Verbox";
import {MainMenu} from "../constants/MainMenu";

const ACTIVE_SUBMENU_CLASSNAME = 'router-link-active';

export default {
    name: "MenuItems",
    mixins: [BrowserStorage, Alerts, Verbox],
    props: {
        collapse: {
            type: Boolean,
            required: false,
            default: false,
        },
        isUserAuth: {
            type: Boolean,
            required: true,
        },
        isMobile: {
            type: Boolean,
            required: false,
            default: false,
        },
        favoriteItems: {
            type: Array,
            required: true,
        },
        isLeftMenu: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            expandedKeys: {},
            favoriteExpandedKeys: {},
            // для скролла submenu в collapsed, когда выходит за рамки окна (высчитывается JS)
            maxHeightCollapsedSubmenu: '100vh',
            // отсчёт sticky обычного меню от верха, чтобы был фиксирован "Избранное" (высчитывается JS)
            mainMenuTop: 0,
            model: {
                favoriteItemsIds: [],
            },
            /** как в SubmenuTopHelp: пункт «Поддержка» только при !user.elamaId */
            showSupport: false,

            // элементы для неавторизованного пользователя
            guestItems: [
                {
                    label: this.$t('lang.menu.guest.site'),
                    key: this.getPath(''),
                    url: this.getPath(''),
                    icon: 'ksi ksi-sites-main',
                    class: this.getIsActiveGuestClass(''),
                },
                {
                    label: this.$t('lang.menu.guest.links'),
                    key: this.getPath('links'),
                    url: this.getPath('links'),
                    icon: 'ksi ksi-links-main',
                    class: this.getIsActiveGuestClass('links'),
                },
                {
                    label: this.$t('lang.menu.guest.monitoring'),
                    key: this.getPath('monitoring'),
                    url: this.getPath('monitoring'),
                    icon: 'ksi ksi-monitoring-list',
                    class: this.getIsActiveGuestClass('monitoring'),
                },
                {
                    label: this.$t('lang.menu.guest.wordstat'),
                    key: this.getPath('wordstat'),
                    url: this.getPath('wordstat'),
                    class: this.getIsActiveClass('wordstat'),
                    icon: 'ksi ksi-wordstat-list',
                },
                {
                    label: this.$t('lang.menu.guest.clustering'),
                    key: this.getPath('clustering'),
                    url: this.getPath('clustering'),
                    class: this.getIsActiveClass('clustering'),
                    icon: 'ksi ksi-clustering-list',
                },
                {
                    label: this.$t('lang.menu.guest.ai_tracker'),
                    key: this.getPath('ai-tracker'),
                    url: this.getPath('ai-tracker'),
                    class: this.getIsActiveClass('ai-tracker'),
                    icon: 'ksi ksi-ai-tracker-list',
                    isNew: true,
                },
                {
                    label: this.$t('lang.menu.guest.suggest'),
                    key: this.getPath('suggest'),
                    url: this.getPath('suggest'),
                    class: this.getIsActiveClass('suggest'),
                    icon: 'ksi ksi-tools-suggest',
                },
                {
                    label: this.$t('lang.menu.guest.yandex_direct'),
                    key: this.getPath('yandex-direct'),
                    url: this.getPath('yandex-direct'),
                    class: this.getIsActiveClass('yandex-direct'),
                    icon: 'ksi ksi-yandex-direct',
                },
                {
                    label: this.$t('lang.menu.guest.rsya'),
                    key: this.getPath('rsya'),
                    url: this.getPath('rsya'),
                    class: this.getIsActiveClass('rsya'),
                    icon: 'ksi ksi-rsya',
                },
                {
                    separator: {
                        separator: true,
                    },
                },
                {
                    label: this.$t('lang.menu.guest.tools'),
                    key: this.getPath('tools'),
                    url: this.getPath('tools'),
                    class: this.getIsActiveClass('tools'),
                    icon: 'ksi ksi-tools',
                },
                {
                    label: this.$t('lang.menu.help_label'),
                    key: 'help-label',
                    icon: 'ksi ksi-help-main',
                    class: 'block md:hidden',
                }
            ],
            // для авторизованных пользователей
            favorites: {
                label: this.$t('lang.menu.favorites'),
                key: 'favorites-main',
                icon: 'ksi ksi-favorites',
            },
            items: {
                projects: {
                    label: this.$t('lang.menu.projects'),
                    key: this.getPath('projects'),
                    url: this.getPath('projects'),
                    icon: 'ksi ksi-projects',
                    class: this.getIsActiveClass('projects'),
                    itemId: MenuItemsIds.PROJECTS,
                },
                sites: {
                    label: this.$t('lang.menu.sites_label'),
                    key: 'sites-main',
                    icon: 'ksi ksi-sites-main',
                    items: [
                        {
                            label: this.$t('lang.menu.group'),
                            key: this.getPath('group'),
                            url: this.getPath('group'),
                            class: this.getIsActiveClass('group'),
                            icon: 'ksi ksi-group',
                            itemId: MenuItemsIds.GROUP,
                        },
                        {
                            label: this.$t('lang.menu.site_compare'),
                            key: this.getPath('compare'),
                            url: this.getPath('compare'),
                            class: this.getIsActiveClass('compare'),
                            icon: 'ksi ksi-compare',
                            itemId: MenuItemsIds.COMPARE,
                        },
                        {
                            label: this.$t('lang.menu.tools_domains_batch'),
                            key: this.getPath('tools/domains-batch'),
                            url: this.getPath('tools/domains-batch'),
                            class: this.getIsActiveClass('tools/domains-batch'),
                            icon: 'ksi ksi-tools-domains-batch',
                            itemId: MenuItemsIds.TOOLS_DOMAINS_BATCH,
                        },
                        {
                            label: this.$t('lang.menu.tools_concurents_by_keywords'),
                            key: this.getPath('tools/concurents-by-keywords'),
                            url: this.getPath('tools/concurents-by-keywords'),
                            class: this.getIsActiveClass('tools/concurents-by-keywords'),
                            icon: 'ksi ksi-tools-concurents-by-keywords',
                            itemId: MenuItemsIds.TOOLS_CONCURENTS_BY_KEYWORDS,
                        },
                        {
                            label: this.$t('lang.menu.tools_site_themes'),
                            key: this.getPath('tools/site-themes'),
                            url: this.getPath('tools/site-themes'),
                            class: this.getIsActiveClass('tools/site-themes'),
                            icon: 'ksi ksi-tools-site-themes',
                            itemId: MenuItemsIds.TOOLS_SITE_THEMES,
                        },
                        {
                            label: this.$t('lang.menu.tools_keywords_by_pages'),
                            key: this.getPath('tools/keywords-by-pages'),
                            url: this.getPath('tools/keywords-by-pages'),
                            class: this.getIsActiveClass('tools/keywords-by-pages'),
                            icon: 'ksi ksi-tools-keywords-by-pages',
                            itemId: MenuItemsIds.TOOLS_KEYWORDS_BY_PAGES,
                        },
                        {
                            label: this.$t('lang.menu.tools_dictionary_ext_by_page'),
                            key: this.getPath('tools/dictionary-ext-by-page'),
                            url: this.getPath('tools/dictionary-ext-by-page'),
                            class: this.getIsActiveClass('tools/dictionary-ext-by-page'),
                            icon: 'ksi ksi-tools-dictionary-ext-by-page',
                            itemId: MenuItemsIds.TOOLS_DICTIONARY_EXT_BY_PAGE,
                        },
                        {
                            label: this.$t('lang.menu.tools_dictionary_by_pages'),
                            key: this.getPath('tools/dictionary-by-pages'),
                            url: this.getPath('tools/dictionary-by-pages'),
                            class: this.getIsActiveClass('tools/dictionary-by-pages'),
                            icon: 'ksi ksi-tools-dictionary-by-pages',
                            itemId: MenuItemsIds.TOOLS_DICTIONARY_BY_PAGES,
                        },
                        {
                            label: this.$t('lang.menu.robots'),
                            key: this.getPath('robots'),
                            url: this.getPath('robots'),
                            class: this.getIsActiveClass('robots'),
                            icon: 'ksi ksi-robots',
                            itemId: MenuItemsIds.ROBOTS,
                        },
                    ],
                },
                links: {
                    label: this.$t('lang.menu.links_label'),
                    key: 'links-main',
                    icon: 'ksi ksi-links-main',
                    items: [
                        {
                            label: this.$t('lang.menu.links'),
                            key: this.getPath('links'),
                            url: this.getPath('links'),
                            class: this.getIsActiveClass('links'),
                            icon: 'ksi ksi-links',
                            itemId: MenuItemsIds.LINKS,
                        },
                        {
                            label: this.$t('lang.menu.links_domains_batch'),
                            key: this.getPath('links/domains-batch'),
                            url: this.getPath('links/domains-batch'),
                            class: this.getIsActiveClass('links/domains-batch'),
                            icon: 'ksi ksi-links-domains-batch',
                            itemId: MenuItemsIds.LINKS_DOMAINS_BATCH,
                        },
                        {
                            label: this.$t('lang.menu.links_site_compare'),
                            url: this.getPath('compare?view=backlinks'),
                            key: this.getPath('compare?view=backlinks'),
                            class: this.getIsActiveClass('compare?view=backlinks'),
                            icon: 'ksi ksi-compare',
                            itemId: MenuItemsIds.COMPARE_BACKLINKS,
                        },
                    ],
                },
                keywords: {
                    label: this.$t('lang.menu.keywords_label'),
                    key: 'keywords-main',
                    icon: 'ksi ksi-keywords-main',
                    items: [
                        {
                            label: this.$t('lang.menu.search_keys'),
                            key: this.getPath('searchkeys'),
                            url: this.getPath('searchkeys'),
                            class: this.getIsActiveClass('searchkeys'),
                            icon: 'ksi ksi-searchkeys',
                            itemId: MenuItemsIds.SEARCHKEYS,
                        },
                        {
                            label: this.$t('lang.menu.similar_keys'),
                            key: this.getPath('similarkeys'),
                            url: this.getPath('similarkeys'),
                            class: this.getIsActiveClass('similarkeys'),
                            icon: 'ksi ksi-similarkeys',
                            itemId: MenuItemsIds.SIMILARKEYS,
                        },
                        {
                            label: this.$t('lang.menu.tools_extended_keywords'),
                            key: this.getPath('tools/extended-keywords'),
                            url: this.getPath('tools/extended-keywords'),
                            class: this.getIsActiveClass('tools/extended-keywords'),
                            icon: 'ksi ksi-tools-extended-keywords',
                            itemId: MenuItemsIds.TOOLS_EXTENDED_KEYWORDS,
                        },
                        {
                            label: this.$t('lang.menu.tools_check_top'),
                            key: this.getPath('tools/check-top'),
                            url: this.getPath('tools/check-top'),
                            class: this.getIsActiveClass('tools/check-top'),
                            icon: 'ksi ksi-tools-check-top',
                            itemId: MenuItemsIds.TOOLS_CHECK_TOP,
                        },
                        {
                            label: this.$t('lang.menu.tools_history_serp'),
                            key: this.getPath('tools/history-serp'),
                            url: this.getPath('tools/history-serp'),
                            class: this.getIsActiveClass('tools/history-serp'),
                            icon: 'ksi ksi-tools-history-serp',
                            itemId: MenuItemsIds.TOOLS_HISTORY_SERP,
                        },
                        {
                            label: this.$t('lang.menu.tools_keywords_by_list'),
                            key: this.getPath('tools/keywords-by-list'),
                            url: this.getPath('tools/keywords-by-list'),
                            class: this.getIsActiveClass('tools/keywords-by-list'),
                            icon: 'ksi ksi-tools-keywords-by-list',
                            itemId: MenuItemsIds.TOOLS_KEYWORDS_BY_LIST,
                        },
                        {
                            label: this.$t('lang.menu.tools_compare'),
                            key: this.getPath('tools/compare'),
                            url: this.getPath('tools/compare'),
                            class: this.getIsActiveClass('tools/compare'),
                            icon: 'ksi ksi-tools-compare',
                            itemId: MenuItemsIds.TOOLS_COMPARE,
                        },
                        {
                            label: this.$t('lang.menu.tools_combine'),
                            key: this.getPath('tools/combine'),
                            url: this.getPath('tools/combine'),
                            class: this.getIsActiveClass('tools/combine'),
                            icon: 'ksi ksi-tools-combine',
                            itemId: MenuItemsIds.TOOLS_COMBINE,
                        },
                        {
                            label: this.$t('lang.menu.tools_delete_double'),
                            key: this.getPath('tools/delete-double'),
                            url: this.getPath('tools/delete-double'),
                            class: this.getIsActiveClass('tools/delete-double'),
                            icon: 'ksi ksi-tools-delete-double',
                            itemId: MenuItemsIds.TOOLS_DELETE_DOUBLE,
                        },
                        {
                            label: this.$t('lang.menu.tools_unique'),
                            key: this.getPath('tools/unique'),
                            url: this.getPath('tools/unique'),
                            class: this.getIsActiveClass('tools/unique'),
                            icon: 'ksi ksi-tools-unique',
                            itemId: MenuItemsIds.TOOLS_UNIQUE,
                        },
                        {
                            label: this.$t('lang.menu.tools_suggest'),
                            key: this.getPath('tools/suggest'),
                            url: this.getPath('tools/suggest'),
                            class: this.getIsActiveClass('tools/suggest'),
                            icon: 'ksi ksi-tools-suggest',
                            itemId: MenuItemsIds.TOOLS_SUGGEST,
                        },
                        {
                            label: this.$t('lang.menu.wordstat_list'),
                            key: this.getPath('wordstat-list'),
                            url: this.getPath('wordstat-list'),
                            class: this.getIsActiveClass('wordstat-list'),
                            icon: 'ksi ksi-wordstat-list',
                            itemId: MenuItemsIds.WORDSTAT,
                        },
                        {
                            label: this.$t('lang.menu.serp_list'),
                            key: this.getPath('serp-list'),
                            url: this.getPath('serp-list'),
                            class: this.getIsActiveClass('serp-list'),
                            icon: 'ksi ksi-serp-list',
                            itemId: MenuItemsIds.SERP,
                        },
                    ]
                },
                clustering: {
                    label: this.$t('lang.menu.clustering_list'),
                    key: this.getPath('clustering-list'),
                    url: this.getPath('clustering-list'),
                    class: this.getIsActiveClass('clustering-list'),
                    icon: 'ksi ksi-clustering-list',
                    itemId: MenuItemsIds.CLUSTERING,
                },
                ads: {
                    label: this.$t('lang.menu.ads_label'),
                    key: 'ads-main',
                    icon: 'ksi ksi-ads-main',
                    items: [
                        {
                            label: this.$t('lang.menu.yandex_direct'),
                            key: this.getPath('yandex-direct'),
                            url: this.getPath('yandex-direct'),
                            class: this.getIsActiveClass('yandex-direct'),
                            icon: 'ksi ksi-yandex-direct',
                            itemId: MenuItemsIds.YANDEX_DIRECT,
                        },
                        {
                            label: this.$t('lang.menu.yandex_direct_archive'),
                            key: this.getPath('yandex-direct-archive'),
                            url: this.getPath('yandex-direct-archive'),
                            class: this.getIsActiveClass('yandex-direct-archive'),
                            icon: 'ksi ksi-yandex-direct',
                            itemId: MenuItemsIds.YANDEX_DIRECT_ARCHIVE,
                        },
                        {
                            label: this.$t('lang.menu.rsya'),
                            key: this.getPath('rsya'),
                            url: this.getPath('rsya'),
                            class: this.getIsActiveClass('rsya'),
                            icon: 'ksi ksi-rsya',
                            itemId: MenuItemsIds.RSYA,
                        },
                    ]
                },
                monitoring: {
                    label: this.$t('lang.menu.monitoring_list'),
                    key: this.getPath('monitoring-list'),
                    url: this.getPath('monitoring-list'),
                    class: this.getIsActiveClass('monitoring-list'),
                    icon: 'ksi ksi-monitoring-list',
                    itemId: MenuItemsIds.MONITORING,
                },
                aiTracker: {
                    label: this.$t('lang.menu.ai_tracker'),
                    key: this.getPath('ai-tracker-list'),
                    url: this.getPath('ai-tracker-list'),
                    class: this.getIsActiveClass('ai-tracker-list'),
                    icon: 'ksi ksi-ai-tracker-list',
                    itemId: MenuItemsIds.AI_TRACKER,
                    isNew: true,
                },
                separator: {
                    separator: true,
                },
                tools: {
                    label: this.$t('lang.menu.tools'),
                    key: this.getPath('tools'),
                    url: this.getPath('tools'),
                    class: this.getIsActiveClass('tools'),
                    icon: 'ksi ksi-tools',
                    itemId: MenuItemsIds.TOOLS,
                },
                // Мобильные пункты
                tariff: {
                    label: this.$t('lang.menu.tariff'),
                    key: this.getPath('tarif'),
                    url: this.getPath('tarif'),
                    icon: 'ksi ksi-tarif',
                    class: [
                        this.getIsActiveClass('tarif'),
                        'block md:hidden',
                    ],
                    itemId: MenuItemsIds.TARIFF,
                },
                help: {
                    label: this.$t('lang.menu.help_label'),
                    key: 'help-label',
                    icon: 'ksi ksi-help-main',
                    class: 'block md:hidden',
                }
            }
        };
    },
    created() {
        const validIdsSet = new Set(Object.values(MenuItemsIds));
        // получаем только те значения, которые валидные (мало ли удалили инструмент)
        this.model.favoriteItemsIds = this.favoriteItems.filter(item => validIdsSet.has(item));
    },
    mounted() {
        // expanded только в полном виде (не схлопнутом)
        if (!this.collapse) {
            this.calcExpanded();
            // сохранено ли раскрытие "Избранное"?
            this.favoriteExpandedKeys = {
                'favorites-main': this.getLocalStorageItem(StorageKeys.LEFT_MENU_FAVORITE_EXPANDED) ?? true
            };
        }

        nextTick(() => {
            this.calcTopMainMenu();
        });

        this.syncShowSupportFromUser(this.$store?.getters?.USER);
        this.$emitter.on(Listeners.GLOBAL_STORE_GET_USER, this.syncShowSupportFromUser);

        this.$emitter.on(Listeners.LEFT_MENU_ADD_FAVORITE, itemId => this.onAddFavorite(itemId));
        this.$emitter.on(Listeners.LEFT_MENU_REMOVE_FAVORITE, itemId => this.onRemoveFavorite(itemId));
    },
    computed: {
        helpSubmenuItems() {
            if (this.isUserAuth) {
                const items = [
                    {
                        label: this.$t('lang.menu.help'),
                        key: '//help.keys.so',
                        url: '//help.keys.so',
                        icon: 'ksi ksi-help',
                        itemId: MenuItemsIds.HELP,
                    },
                    {
                        label: this.$t('lang.menu.api'),
                        key: 'api',
                        url: this.$config.DOC_API_URL,
                        icon: 'ksi ksi-api',
                        itemId: MenuItemsIds.API,
                    },
                    {
                        label: this.$t('lang.menu.blog'),
                        key: '//blog.keys.so',
                        url: '//blog.keys.so',
                        icon: 'ksi ksi-blog',
                        itemId: MenuItemsIds.BLOG,
                    },
                    {
                        label: this.$t('lang.menu.faq'),
                        key: '//help.keys.so/faq',
                        url: '//help.keys.so/faq',
                        icon: 'ksi ksi-faq',
                        itemId: MenuItemsIds.FAQ,
                    },
                    {
                        label: this.$t('lang.menu.demo'),
                        key: 'demo',
                        icon: 'pi pi-video',
                        onClick: this.onClickDemo,
                    },
                ];
                if (this.showSupport) {
                    items.push({
                        label: this.$t('lang.menu.support'),
                        key: 'support',
                        icon: 'pi pi-user',
                        onClick: this.onClickSupport,
                    });
                }
                return items;
            }

            const items = [
                {
                    label: this.$t('lang.menu.help'),
                    key: '//help.keys.so',
                    url: '//help.keys.so',
                    icon: 'ksi ksi-help',
                },
                {
                    label: this.$t('lang.menu.api'),
                    key: 'api',
                    url: this.$config.DOC_API_URL,
                    icon: 'ksi ksi-api',
                },
                {
                    label: this.$t('lang.menu.blog'),
                    key: '//blog.keys.so',
                    url: '//blog.keys.so',
                    icon: 'ksi ksi-blog',
                },
                {
                    label: this.$t('lang.menu.faq'),
                    key: '//help.keys.so/faq',
                    url: '//help.keys.so/faq',
                    icon: 'ksi ksi-faq',
                },
                {
                    label: this.$t('lang.menu.demo'),
                    key: 'demo',
                    icon: 'pi pi-video',
                    onClick: this.onClickDemo,
                },
            ];
            if (this.showSupport) {
                items.push({
                    label: this.$t('lang.menu.support'),
                    key: 'support',
                    icon: 'pi pi-user',
                    onClick: this.onClickSupport,
                });
            }
            return items;
        },
        getModel() {
            if (!this.isUserAuth) {
                return this.guestItems.map(item => (
                    item.key === 'help-label'
                        ? {...item, items: this.helpSubmenuItems}
                        : item
                ));
            }

            return [
                this.items.projects,
                this.items.sites,
                this.items.links,
                this.items.keywords,
                this.items.clustering,
                this.items.ads,
                this.items.monitoring,
                this.items.aiTracker,
                this.items.separator,
                this.items.tools,
                this.items.tariff,
                {...this.items.help, items: this.helpSubmenuItems},
            ];
        },
        getFavoriteModel() {
            const itemsTree = {
                ...this.items,
                help: {...this.items.help, items: this.helpSubmenuItems},
            };
            this.favorites.items = this.getFavoriteItems(itemsTree, this.model.favoriteItemsIds);

            return [this.favorites];
        },
        getMainMenuTop() {
            return this.mainMenuTop;
        },
        hasFavoriteItems() {
            return this.favorites.items.length > 0;
        },
    },
    methods: {
        // подтягиваем объекты избранных пунктов по их ID
        getFavoriteItems(obj, targetItemIds) {
            const result = [];

            const traverse = (value) => {
                if (Array.isArray(value)) {
                    value.forEach(traverse);
                } else if (value && typeof value === 'object') {
                    if (
                        'itemId' in value
                        && typeof value.itemId === 'number'
                        && targetItemIds.includes(value.itemId)
                    ) {
                        result.push(value);
                    }

                    Object.values(value).forEach(traverse);
                }
            };

            traverse(obj);
            return result;
        },
        getPath(path) {
            return sprintf('/%s/%s', window.locale, path);
        },
        getIsActiveClass(childItemKey) {
            // пункты меню, которые имеют одинаковый url или кастомные правила
            const customActive = this.checkCustomActive(childItemKey);
            if (customActive !== undefined && customActive !== null) {
                return customActive;
            }

            // основное правило, "начинается с" — по границе "/": /ru/yandex-direct-archive не зажигает yandex-direct
            const path = this.getPath(childItemKey);
            const pathname = window.location.pathname;
            return pathname === path || pathname.startsWith(path + '/') ? ACTIVE_SUBMENU_CLASSNAME : '';
        },
        // активность ссылок для Гостя
        getIsActiveGuestClass(itemKey) {
            // обрезание слэша в конце необходимо в основном для главной страницы
            return window.location.pathname === this.getPath(itemKey).replace(/\/+$/, '') ? ACTIVE_SUBMENU_CLASSNAME : '';
        },
        checkCustomActive(childItemKey) {
            if (window.location.pathname === this.getPath('links/domains-batch')) {
                switch (childItemKey) {
                    case 'links/domains-batch':
                        return ACTIVE_SUBMENU_CLASSNAME;
                    case 'links':
                        return '';
                }
            }

            if (childItemKey === 'tools' && window.location.pathname !== this.getPath('tools')) {
                return '';
            }

            if (window.location.hash === '#block-links' && childItemKey === 'links') {
                return ACTIVE_SUBMENU_CLASSNAME;
            }

            if (window.location.pathname === this.getPath('compare')) {
                const params = new URLSearchParams(window.location.search);
                const isBacklinksView = params.get('view') === 'backlinks';

                switch (true) {
                    case (childItemKey === 'compare' && !isBacklinksView):
                        return ACTIVE_SUBMENU_CLASSNAME;
                    case (childItemKey === 'compare?view=backlinks' && isBacklinksView):
                        return ACTIVE_SUBMENU_CLASSNAME;
                    default:
                        return '';
                }
            }
        },
        calcExpanded() {
            // пункты меню, которые имеют одинаковый url или кастомные правила
            const customExpended = this.checkCustomExpended();
            if (Object.keys(customExpended).length) {
                this.expandedKeys = customExpended;
                return;
            }

            const parents = ArrayHelper.getParentsObjectsByStartWithParamValue(
                this.getModel,
                window.location.pathname,
                'key',
                'items'
            );

            let expanded = {};
            // проверяем, не является ли данная страница родительской
            for (const [key, item] of Object.entries(this.getModel)) {
                if (window.location.pathname.startsWith(item.key)) {
                    expanded[item.key] = true;
                }
            }
            // ищем по дочерним пунктам
            for (const [key, item] of Object.entries(parents)) {
                expanded[item.key] = true;
            }

            this.expandedKeys = expanded;
        },
        checkCustomExpended() {
            let customExpanded = {};
            const params = new URLSearchParams(window.location.search);
            const isBacklinksView = params.get('view') === 'backlinks';

            if (
                (window.location.pathname === this.getPath('compare') && isBacklinksView)
                || (window.location.pathname === this.getPath('report') && window.location.hash === '#block-links')
            ) {
                customExpanded['links-main'] = true;
            }

            return customExpanded;
        },
        updateExpandedKeys(event) {
            // в схлопнутом виде не раскрываются подпункты
            if (this.collapse) {
                this.expandedKeys = {};
                return;
            }

            this.expandedKeys = event;
        },
        // расчёт максимальной высоты для скролла сабменю в схлопнутом виде
        calcMaxHeightCollapsedSubmenu(event) {
            const rect = event.target.getBoundingClientRect();
            const offsetTop = rect.top; // расстояние от верха вьюпорта
            this.maxHeightCollapsedSubmenu = 'calc(100vh - ' + offsetTop + 'px)';
        },
        // расчёт CSS top для учёта "Избранное"
        calcTopMainMenu() {
            setTimeout(() => {
                const rect = this.$refs.favoriteDivider?.getBoundingClientRect();
                if (!rect) {
                    return;
                }
                const offsetTop = rect.top; // расстояние от верха вьюпорта
                this.mainMenuTop = offsetTop + 'px';
            }, 500);
        },
        onClickSettings() {
            this.$emitter.emit(Listeners.LEFT_MENU_SETTINGS, {
                items: this.getModel,
                favoriteItems: this.getFavoriteModel,
                isLeftMenu: this.isLeftMenu,
            });
        },
        onAddFavorite(itemId) {
            if (this.model.favoriteItemsIds.length >= MainMenu.FAVORITE_ITEMS_LIMIT) {
                this.alertInfo(this.$t('lang.menu.max_favorite_limits'));
                return;
            }

            this.model.favoriteItemsIds.push(itemId);

            this.updateFavoriteItems();
        },
        onRemoveFavorite(itemId) {
            const index = this.model.favoriteItemsIds.indexOf(itemId);
            if (index !== -1) {
                this.model.favoriteItemsIds.splice(index, 1);
            }

            this.updateFavoriteItems();
        },
        onClickCollapseButton() {
            this.favoriteExpandedKeys = {};
            this.$emitter.emit(Listeners.LEFT_MENU_CLICK);
            nextTick(() => {
                this.calcTopMainMenu();
            });
        },
        onToggleFavoriteExpanded(event) {
            if (!this.favorites.items.length) {
                this.onClickSettings();
                return;
            }

            // в схлопнутом виде не раскрываются подпункты
            if (this.collapse) {
                this.favoriteExpandedKeys = {};
                return;
            }

            this.setLocalStorageItem(StorageKeys.LEFT_MENU_FAVORITE_EXPANDED, Object.keys(event).length);
            this.favoriteExpandedKeys = event;
            this.mainMenuTop = 'auto';
            nextTick(() => {
                this.calcTopMainMenu();
            });
        },
        updateFavoriteItems: debounce(function() {
            UserApi
                .setSettings({
                    menuFavoriteItems: this.model.favoriteItemsIds,
                })
                .catch(error => this.alertError(error));
        }, AppConstant.TIMEOUT_DEBOUNCE),
        onMenuItemClick(event, item) {
            if (!item || typeof item.onClick !== 'function') {
                return;
            }
            event.preventDefault();
            item.onClick();
            if (this.isMobile) {
                this.$emitter.emit(Listeners.LEFT_MENU_MOBILE_CLOSE);
            }
        },
        onClickDemo() {
            this.$emitter.emit(Listeners.POPUP_DEMO_BLOCK_OPEN);
        },
        onClickSupport() {
            this.onOpenVerbox();
        },
        syncShowSupportFromUser(user) {
            if (user) {
                this.showSupport = !user.elamaId;
            }
        },
    },
    watch: {
        collapse() {
            this.updateExpandedKeys();
        },
    },
    components: {
        PanelMenu,
        Divider,
        MenuSettings,
    },
}
</script>

<style scoped>
.menu_items__container {
    position: sticky;
    top: var(--keysso-top-menu-height);
    max-height: calc(100vh - var(--keysso-top-menu-height));
    height: 100%;
    width: 250px;
    padding: 0 5px;
    background-color: #33404D;
}
.menu_items__collapsed {
    width: auto;
}
.menu_items__item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    column-gap: 8px;
    padding: 12px 14px;
    cursor: pointer;
    text-decoration: none;
    position: relative;
}
.menu_items__item .menu_items__item_label {
    display: flex;
    align-items: center;
    column-gap: 8px;
    text-decoration: none;
}
.menu_items__item span {
    font-size: 13px;
    font-weight: 400;
    color: #CED4DA;
    line-height: 1.2;
}
.menu_items__item .menu_items__item_label i {
    font-size: 24px;
    color: #A4B1BE;
}
.menu_items__item i {
    font-size: 13px;
    color: #6C757D;
}

.menu_items__favorite_divider {
    position: relative;
    z-index: 0;
}
/* стилизация скролла для меню */
div::-webkit-scrollbar {
    width: 3px; /* Ширина вертикального скроллбара */
    height: 3px; /* Высота горизонтального скроллбара */
    border-radius: 3px; /* Закруглённые края */
}
/* Ползунок (бегунок) */
div::-webkit-scrollbar-thumb {
    background: #586A84; /* Цвет бегунка */
    border-radius: 3px; /* Закруглённые края */
}
/* Ползунок при наведении */
div::-webkit-scrollbar-thumb:hover {
    background: #586A84;
}
/* Фон скроллбара */
div::-webkit-scrollbar-track {
    background: #475569;
    border-radius: 3px;
}
/* new, beta иконки */
.menu_items__item_new:after,
.menu_items__item_beta:after {
    padding: 1px 4px;
    border-radius: 2px;
    line-height: 1;
    font-weight: 700;
    font-size: 10px;
    color: #fff;
    letter-spacing: 0.1px;
}
.menu_items__item_new:after {
    content: 'новое';
    background-color: #70b319;
}
.menu_items__item_beta:after {
    content: 'бета';
    background-color: orange;
}

/* collapsed */
.menu_items__collapsed .menu_items__item_new:after,
.menu_items__collapsed .menu_items__item_beta:after {
    content: none;
}
.menu_items__item_collapsed {
    display: flex;
    justify-content: space-between;
    align-items: center;
    column-gap: 8px;
    text-decoration: none;
    position: relative;
}
.menu_items__item_collapsed .menu_items__item_label {
    display: flex;
    align-items: center;
    column-gap: 8px;
    text-decoration: none;
}
.menu_items__item_collapsed > .menu_items__item_label {
    padding: 12px 14px;
    border-top-left-radius: var(--border-radius);
    border-bottom-left-radius: var(--border-radius);
    overflow: hidden;
}
.menu_items__item_collapsed span {
    font-size: 13px;
    font-weight: 400;
    color: #CED4DA;
    line-height: 1.2;
}
.menu_items__item_collapsed .menu_items__item_label i {
    font-size: 24px;
    color: #A4B1BE;
}
.menu_items__item_collapsed i {
    font-size: 13px;
    color: #6C757D;
}
.menu_items__collapsed_submenu {
    display: none;
    position: absolute;
    top: 0;
    left: 100%;
    flex-direction: column;
    background-color: #556275;
    padding: 10px;
    border-top-right-radius: var(--border-radius);
    border-bottom-right-radius: var(--border-radius);
    max-height: v-bind(maxHeightCollapsedSubmenu);
    overflow: auto;
}
.menu_items__collapsed_submenu.menu_items__collapsed_with_items {
    border-bottom-left-radius: var(--border-radius);
}
.menu_items__item_collapsed:hover .menu_items__collapsed_submenu {
    display: flex;
    transition: all .3s;
}
.menu_items__collapsed_submenu .menu_items__item_label {
    padding: 6px 8px;
    white-space: nowrap;
}
.menu_items__collapsed_submenu .menu_items__collapsed_title {
    font-weight: 700;
    color: #fff;
}
.menu_items__icon_settings_container {
    position: fixed;
    bottom: 0;
    width: var(--keysso-left-menu-width);
    display: flex;
    justify-content: center;
    padding: 16px 10px 16px 6px;
    left: 0;
    gap: 8px;
    background-color: #33404D;
    z-index: 1;
    font-size: 13px;
}
.menu_items__icon_settings_container i {
    color: #A4B1BE;
    cursor: pointer;
}
.menu_items__icon_settings_container span {
    color: #CED4DA;
    cursor: pointer;
}

.menu_items__button_collapse {
    position: absolute;
    right: -17px;
    bottom: -24px;
    width: 24px;
    height: 46px;
    background-color: #33404D;
    display: flex;
    align-items: center;
    justify-content: center;
    clip-path: path("M24 21.8757C24 11 12 13.026 12 0V44C12 33.4102 24 33.5 24 21.8757Z M0 21.8757C0 11 12 13.026 12 0V44C12 33.4102 0 33.5 0 21.8757Z");
    cursor: pointer;
}
.menu_items__button_collapse i {
    color: #fff;
    font-size: 12px;
}
i.pi-video {
    font-size: 20px !important;
    margin-left: 3px;
    margin-right: 1px;
}
i.pi-user {
    font-size: 20px !important;
    margin-left: 3px;
    margin-right: 3px;
}
</style>
<style>
.menu_items__container .p-panelmenu {
    padding: 0 5px;
    z-index: 1;
    position: relative;
}
.menu_items__menu_container.p-panelmenu {
    overflow: auto;
    height: calc(100vh - v-bind(getMainMenuTop) - 60px);
    padding: 0 5px;
}
.menu_items__container .p-panelmenu-header-content,
.menu_items__container .p-panelmenu-content {
    border: none !important;
}
.menu_items__container .p-panelmenu-content {
    padding: 0 !important;
}
.menu_items__container .p-panelmenu-header .p-panelmenu-header-content {
    background-color: #33404D !important;
}
.menu_items__container .p-panelmenu-header:not(.p-disabled).p-highlight .p-panelmenu-header-content,
.menu_items__container .p-panelmenu-content,
.menu_items__container .p-menuitem-content {
    background-color: #475569 !important;
}
/* hover */
.menu_items__container .menu_items__item,
.menu_items__container .menu_items__item span,
.menu_items__container .menu_items__item i,
.menu_items__container .menu_items__item_collapsed,
.menu_items__container .menu_items__item_collapsed span,
.menu_items__container .menu_items__item_collapsed i {
    transition: all .3s;
}
.menu_items__container .menu_items__item:hover,
.menu_items__container .menu_items__item_collapsed:hover > .menu_items__item_label {
    background-color: #556275;
}
.menu_items__container .menu_items__item:hover > .menu_items__item_label span,
.menu_items__container .menu_items__item:hover > .menu_items__item_label i,
.menu_items__container .menu_items__item_collapsed:hover > .menu_items__item_label span,
.menu_items__container .menu_items__item_collapsed:hover > .menu_items__item_label i,
.menu_items__container .menu_items__collapsed_submenu .menu_items__item_label:hover span,
.menu_items__container .menu_items__collapsed_submenu .menu_items__item_label:hover i {
    color: #fff;
}
/* active */
.menu_items__container .router-link-active .menu_items__item {
    background-color: #4d5e77 !important;
}
.menu_items__container .router-link-active .menu_items__item a {
    color: #fff !important;
}
.menu_items__container .router-link-active .menu_items__item span {
    color: #fff !important;
}
.menu_items__container .router-link-active a i {
    color: #88CC30 !important;
}
.menu_items__container .p-panelmenu-header:not(.p-disabled).p-highlight .menu_items__item > .menu_items__item_label > i,
.menu_items__container .p-panelmenu-header:not(.p-disabled).p-highlight .menu_items__item > .menu_items__item_label > span {
    color: #fff;
}
.menu_items__container .p-panelmenu-panel {
    border-radius: var(--border-radius);
    overflow: hidden;
}
.menu_items__container .p-panelmenu-header-content {
    box-shadow: none !important;
}
/* Divider */
.menu_items__container .p-divider {
    margin: 8px 0 !important;
}
.menu_items__container .p-divider:before {
    border-top: 1px solid #6C757D !important;
}
/* на будущее, для сепаратора в сабменю */
.menu_items__container .p-menuitem-separator {
    border-top: 1px solid #33404d !important;
}
</style>
